import React, { useState, useRef, useEffect } from 'react';
import { industrias, subindustriasPorIndustria } from '../data/standsData';

interface MultiSelectTagsProps {
  selectedValues: string[];
  onChange: (values: string[]) => void;
  error?: string;
}

const MultiSelectTags: React.FC<MultiSelectTagsProps> = ({
  selectedValues,
  onChange,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(industrias);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isMobileTablet, setIsMobileTablet] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      // Mostrar solo las industrias no seleccionadas
      setFilteredOptions(industrias.filter(option => !selectedValues.includes(option)));
    } else {
      const filtered = industrias.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selectedValues.includes(option)
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, selectedValues]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setIsFocused(false);
        setIsExpanded(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus en el input de búsqueda cuando se abre el dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Detectar si es tablet (769px - 1024px) y mobile/tablet (<= 1024px)
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 769 && width <= 1024);
      setIsMobileTablet(width <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    // Limpiar tooltip existente primero
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }

    if (visibleTooltip !== null && tooltipPosition !== null) {
      const subindustrias = subindustriasPorIndustria[visibleTooltip as keyof typeof subindustriasPorIndustria]?.subindustrias || [];
      
      if (subindustrias.length > 0) {
        // Crear el elemento del tooltip
        const tooltipDiv = document.createElement('div');
        tooltipDiv.className = 'tooltip-content';
        // Determinar si estamos en mobile/tablet para hacer el tooltip interactivo
        const isMobileTablet = window.innerWidth <= 1024;
        
        tooltipDiv.style.cssText = `
          position: fixed !important;
          left: ${tooltipPosition.left}px !important;
          top: ${tooltipPosition.top}px !important;
          z-index: 999999 !important;
          background-color: #ffffff !important;
          border: 2px solid #ef4444 !important;
          border-radius: 8px !important;
          padding: 12px 16px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          min-width: 280px !important;
          max-width: 400px !important;
          font-family: Montserrat, sans-serif !important;
          pointer-events: ${isMobileTablet ? 'auto' : 'none'} !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        `;

        // Crear el contenido
        const titleDiv = document.createElement('div');
        titleDiv.style.cssText = 'font-weight: 700; color: #1e293b; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;';
        titleDiv.textContent = 'Incluye:';

        const ul = document.createElement('ul');
        ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';

        subindustrias.forEach((subindustria) => {
          const li = document.createElement('li');
          li.style.cssText = 'font-size: 12px; color: #64748b; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;';
          
          const bullet = document.createElement('span');
          bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
          bullet.textContent = '•';
          
          li.appendChild(bullet);
          li.appendChild(document.createTextNode(subindustria));
          ul.appendChild(li);
        });

        tooltipDiv.appendChild(titleDiv);
        tooltipDiv.appendChild(ul);
        
        if (document.body) {
          document.body.appendChild(tooltipDiv);
          tooltipRef.current = tooltipDiv;
        }

        // Cleanup
        return () => {
          if (tooltipRef.current && tooltipRef.current.parentNode) {
            tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          }
          tooltipRef.current = null;
        };
      }
    }
  }, [visibleTooltip, tooltipPosition]);

  // Efecto para cerrar el tooltip de industrias al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (visibleTooltip !== null && window.innerWidth <= 1024) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Si el clic no es en el tooltip ni en un icono de información, cerrar el tooltip
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(target) &&
          !target.classList.contains('modalidad-info-icon') &&
          !target.closest('.modalidad-info-icon')
        ) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [visibleTooltip]);

  const handleContainerClick = () => {
    setIsOpen(true);
    setIsFocused(true);
    setIsExpanded(true);
  };

  const handleContainerFocus = () => {
    setIsFocused(true);
  };

  const handleContainerBlur = () => {
    // Solo perder focus si no hay dropdown abierto
    if (!isOpen) {
      setIsFocused(false);
      setIsExpanded(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          selectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const selectOption = (option: string) => {
    if (!selectedValues.includes(option)) {
      onChange([...selectedValues, option]);
    }
    setSearchTerm('');
    setHighlightedIndex(-1);
    // Mantener el dropdown abierto para selecciones múltiples
    // setIsOpen(false);
    // setIsFocused(false);
  };

  const removeTag = (tagToRemove: string) => {
    onChange(selectedValues.filter(tag => tag !== tagToRemove));
    // Solo cerrar el campo si estaba cerrado antes de eliminar la chip
    // Si estaba abierto (isExpanded = true), mantenerlo abierto
    if (!isExpanded) {
      setIsFocused(false);
    }
    // No cambiar isExpanded aquí - mantener el estado actual
  };

  const handleOptionClick = (option: string) => {
    selectOption(option);
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }} ref={containerRef}>
      {/* Contenedor de tags */}
      <div
        style={{
          display: 'flex',
          flexWrap: isExpanded ? 'wrap' : 'nowrap',
          alignItems: isExpanded ? 'flex-start' : 'center',
          gap: '6px',
          height: isExpanded ? 'auto' : '38px',
          minHeight: '38px',
          width: '100%',
          maxWidth: '100%',
          padding: '8px 12px',
          border: error ? '1px solid #ef4444' : (isFocused ? '1px solid #CE2B15' : '1px solid #e5e7eb'),
          borderRadius: '6px',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          transition: 'border-color 0.2s ease',
          boxShadow: isFocused && !error ? '0 0 0 2px rgba(206, 43, 21, 0.1)' : 'none',
          transform: isFocused && !error ? 'translateY(-1px)' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
        onClick={handleContainerClick}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        tabIndex={0}
      >
        {/* Tags seleccionados */}
         {selectedValues.map((tag, index) => {
           // Calcular cuántas chips se pueden mostrar sin expandir
           // Tablet: 1 chip, Mobile/Desktop: 2 chips
           const maxVisibleChips = isTablet ? 1 : 2;
           const shouldShowChip = isExpanded || index < maxVisibleChips;
           
           if (!shouldShowChip) return null;
           
           // Truncar el texto de la chip si es muy largo cuando no está expandido
           const displayText = isExpanded ? tag : (tag.length > 14 ? tag.substring(0, 14) + '...' : tag);
           
           return (
             <div
               key={`${tag}-${index}`}
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '2px',
                 padding: '2px 6px',
                 backgroundColor: '#f1f5f9',
                 border: '1px solid #d1d5db',
                 borderRadius: '3px',
                 fontSize: '11px',
                 fontFamily: 'Montserrat, sans-serif',
                 color: '#374151',
                 whiteSpace: 'nowrap',
                 flexShrink: 0,
                 minWidth: 'fit-content',
                 maxWidth: isExpanded ? 'none' : '120px'
               }}
               title={tag} // Mostrar el texto completo en el tooltip
             >
               <span>{displayText}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: '10px',
                  lineHeight: 1,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '12px',
                  height: '12px'
                }}
              >
                ×
              </button>
            </div>
          );
        })}

         {/* Indicador de chips ocultas cuando no está expandido */}
         {!isExpanded && selectedValues.length > (isTablet ? 1 : 2) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 6px',
              backgroundColor: '#e5e7eb',
              border: '1px solid #d1d5db',
              borderRadius: '3px',
              fontSize: '11px',
              fontFamily: 'Montserrat, sans-serif',
              color: '#6b7280',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
              setIsOpen(true);
              setIsFocused(true);
            }}
          >
             +{selectedValues.length - (isTablet ? 1 : 2)} más
          </div>
        )}

        {/* Placeholder cuando no hay tags */}
        {selectedValues.length === 0 && (
          <span
            style={{
              color: '#9ca3af',
              fontSize: '13px',
              fontFamily: 'Montserrat, sans-serif',
              fontStyle: 'italic'
            }}
          >
            Seleccionar industrias...
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            maxHeight: '250px',
            overflow: 'hidden'
          }}
        >
          {/* Buscador dentro del dropdown */}
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder="Buscar industrias..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontFamily: 'Montserrat, sans-serif',
              color: '#374151',
              outline: 'none',
              background: 'transparent'
            }}
          />

          {/* Lista de opciones */}
          <div style={{ maxHeight: '180px', overflow: 'auto' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const subindustrias = subindustriasPorIndustria[option as keyof typeof subindustriasPorIndustria]?.subindustrias || [];
                const hasSubindustrias = subindustrias.length > 0;
                
                return (
                  <div
                    key={option}
                    onClick={() => handleOptionClick(option)}
                    onMouseEnter={(e) => {
                      setHighlightedIndex(index);
                      // Desactivar tooltips en tablet y mobile (<= 1024px)
                      if (hasSubindustrias && window.innerWidth > 1024) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const tooltipWidth = 300;
                        const spacing = 10;
                        
                        // Calcular posición: siempre abajo, centrado horizontalmente
                        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                        let top = rect.bottom + spacing;
                        
                        // Verificar si se sale por la derecha
                        if (left + tooltipWidth > window.innerWidth) {
                          left = window.innerWidth - tooltipWidth - 10;
                        }
                        
                        // Verificar si se sale por la izquierda
                        if (left < 10) {
                          left = 10;
                        }
                        
                        // Verificar si se sale por abajo (si no cabe abajo, mostrar arriba)
                        const tooltipHeight = 200;
                        if (top + tooltipHeight > window.innerHeight) {
                          top = rect.top - tooltipHeight - spacing;
                        }
                        
                        // Verificar si se sale por arriba después del ajuste
                        if (top < 10) {
                          top = 10;
                        }
                        
                        setTooltipPosition({ left, top });
                        setVisibleTooltip(option);
                      }
                    }}
                    onMouseLeave={() => {
                      setVisibleTooltip(null);
                      setTooltipPosition(null);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: index === highlightedIndex ? '#f1f5f9' : 'transparent',
                      color: '#374151',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>{option}</span>
                    {hasSubindustrias && isMobileTablet && (
                      <span
                        className="modalidad-info-icon"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          border: '1px solid #d1d5db',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          marginLeft: '8px'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          
                          // Si el tooltip ya está visible para esta industria, cerrarlo
                          if (visibleTooltip === option) {
                            setVisibleTooltip(null);
                            setTooltipPosition(null);
                            return;
                          }
                          
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltipWidth = 300;
                          const spacing = 10;
                          
                          let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                          let top = rect.bottom + spacing;
                          
                          if (left + tooltipWidth > window.innerWidth) {
                            left = window.innerWidth - tooltipWidth - 10;
                          }
                          
                          if (left < 10) {
                            left = 10;
                          }
                          
                          const tooltipHeight = 200;
                          if (top + tooltipHeight > window.innerHeight) {
                            top = rect.top - tooltipHeight - spacing;
                          }
                          
                          if (top < 10) {
                            top = 10;
                          }
                          
                          setTooltipPosition({ left, top });
                          setVisibleTooltip(option);
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                          e.currentTarget.style.color = '#374151';
                          e.currentTarget.style.borderColor = '#9ca3af';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                          e.currentTarget.style.color = '#6b7280';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                      >
                        ?
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontStyle: 'italic',
                  fontSize: '13px',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                {searchTerm ? 'No se encontraron industrias' : 'Todas las industrias están seleccionadas'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default MultiSelectTags;
